/* eslint-disable no-cond-assign */
/* eslint-disable prefer-destructuring */
import get from 'lodash/get';
import round from 'lodash/round';
import { ProposalDAOInterface } from '../ProposalDAO';
import { ComparisonItemBasedProposalDAO } from '../ProposalDAOImpl';
import * as ProposalNS from '../../Proposal';
import * as ProposalElementNS from '../../ProposalElements';
import { ProposalFieldDAO } from '../ProposalFieldDAO';
import { ComparisonItemDAO } from '../ProposalItemDAO';
import { DataPoints, DataPointResponse, PercentGrowthHasDisclaimer } from '../DataPoints';
import { ProposalStyleClassNames } from '../../ProposalStyleClassNames';
import { NotApplicableCell, PercentageCell, HoldingCell, DateCell, GrowthCell, NoDataCell } from '../../../../views/components/cells';
import { deepClone } from '../../../../utils/objects';
import { formatLocalizedString, formatCurrency, generateLocalizedString } from '../../../../utils/i18n';
import { MERBreakdown } from '../constants';
import Dictionary from '@/types/dictionary';
import DonutData from '@/types/donut-data';
import GrowthTypes from '@/types/growth-types';
import PortfolioHoldingType from '@/types/portfolio-holding-type';
import ComparisonHolding from '@/types/comparison-holding';
import PercentGrowth from '@/types/percent-growth';
import LocalizedString from '@/types/localized-string';
import { HOLDING_TYPES } from '@/utils/portfolios';

/**
 * @class CountryAllocationDAO Data Access Object
 */
export default class PortfolioHoldingsDAO extends ComparisonItemBasedProposalDAO {
  growthPeriods: Array<GrowthTypes> = [];

  labelOverride?: Dictionary<LocalizedString>;

  public static buildInstance(proposal: ProposalNS.Proposal, proposalElement: ProposalElementNS.ProposalDataElement): ProposalDAOInterface {
    return new PortfolioHoldingsDAO(proposal, proposalElement);
  }

  /* eslint-disable-next-line class-methods-use-this */
  protected getColorKeys(): string[] {
    return ['DEFAULT'];
  }

  protected build(): void {
    let groupByAssetType: boolean = false;
    let filterHoldingType: Array<PortfolioHoldingType> = [];
    let showSymbol: boolean = false;
    let addFundName: boolean = false;
    let showInceptionDate: boolean = false;
    let showAmount: boolean = false;
    let showAssetClass: boolean = false;
    let showClassification: boolean = false;
    let showMER: boolean = false;
    let showAllPortfolioTotals: boolean = false;
    let showFundCode: boolean = false;
    let filterCount: number = 10;
    let start: number = 1;
    let fieldAsterisks: string[] = [];
    let showAssetGroupTotals: boolean = true;
    let usePortfolioTotalFee: boolean = true;
    let showRiskRating: boolean = false;

    const daoConfig: ProposalNS.PortfolioHoldingsConfig = this.proposalElement.DAO as ProposalNS.PortfolioHoldingsConfig;

    if (daoConfig.growthPeriods !== undefined) {
      this.growthPeriods = daoConfig.growthPeriods;
    }

    if (daoConfig.filterTopXDAOOptions !== undefined) {
      filterCount = daoConfig.filterTopXDAOOptions.count;

      if (daoConfig.filterTopXDAOOptions.start) {
        start = daoConfig.filterTopXDAOOptions.start;
      }
    }

    if (daoConfig.groupByAssetType !== undefined) {
      groupByAssetType = daoConfig.groupByAssetType;
    }

    if (daoConfig.filterHoldingType !== undefined) {
      filterHoldingType = daoConfig.filterHoldingType;
    }

    if (daoConfig.extraColumnsDAOOptions !== undefined) {
      if (daoConfig.extraColumnsDAOOptions.columns.indexOf('SYMBOL') !== -1) {
        showSymbol = true;
      }

      if (daoConfig.extraColumnsDAOOptions.columns.indexOf('FUND_NAME') !== -1) {
        addFundName = true;
      }

      if (daoConfig.extraColumnsDAOOptions.columns.indexOf('INCEPTION_DATE') !== -1) {
        showInceptionDate = true;
      }

      if (daoConfig.extraColumnsDAOOptions.columns.indexOf('AMOUNT') !== -1) {
        showAmount = true;
      }

      if (daoConfig.extraColumnsDAOOptions.columns.indexOf('ASSET_CLASS') !== -1) {
        showAssetClass = true;
      }

      if (daoConfig.extraColumnsDAOOptions.columns.indexOf('CLASSIFICATION') !== -1) {
        showClassification = true;
      }

      if (daoConfig.extraColumnsDAOOptions.columns.indexOf('MER') !== -1) {
        showMER = true;
      }

      if (daoConfig.extraColumnsDAOOptions.columns.indexOf('FUND_CODE') !== -1) {
        showFundCode = true;
      }

      if (daoConfig.extraColumnsDAOOptions.columns.indexOf('RISK_RATING') !== -1) {
        showRiskRating = true;
      }
    }

    if (daoConfig.fieldAsterisks !== undefined) {
      fieldAsterisks = daoConfig.fieldAsterisks || [];
    }

    if (daoConfig.showAssetGroupTotals !== undefined) {
      showAssetGroupTotals = daoConfig.showAssetGroupTotals;
    }

    if (daoConfig.showAllPortfolioTotals !== undefined) {
      showAllPortfolioTotals = daoConfig.showAllPortfolioTotals;
    }

    if (daoConfig.usePortfolioTotalFee !== undefined) {
      usePortfolioTotalFee = daoConfig.usePortfolioTotalFee;
    }

    if (this.customFieldNames != null) {
      for (const name of this.customFieldNames.names) {
        this.addFieldName(this.formatLocalizedString(name), ProposalStyleClassNames.ProposalTableContent);
      }
    }

    if (daoConfig.labelOverride !== undefined) {
      this.labelOverride = daoConfig.labelOverride;
    }

    const hasClassEnabled = showAssetClass || showClassification;
    let itemDAO: ComparisonItemDAO | null = null;
    while ((itemDAO = this.nextItem()) !== null) {
      if (groupByAssetType) {
        const dpResponse: DataPointResponse<Dictionary<ComparisonHolding[]>> = DataPoints.getHoldingsByAssetGroup(itemDAO.item, hasClassEnabled);
        const { locale } = this.proposal.options;
        const keys: string[] = Object.keys(dpResponse.response);

        if (keys.length !== 0) {
          this.addDisclaimerKeys(dpResponse.disclaimerKeys, itemDAO.item);
        }

        let grandTotalAmount: number = 0;
        let totalWeightFieldIndex = -1;
        let totalAmountFieldIndex = -1;
        let merFieldIndex = -1;
        let growthStartFieldIndex = -1;

        for (const key of keys) {
          let weightTotal: number = 0;
          let amountTotal: number = 0;
          let rowIndex = 0;

          const comparisonHoldings = dpResponse.response[key].slice();

          // Sub-group items by their classification
          comparisonHoldings.sort((a, b) => {
            const formatted = [formatLocalizedString(a.classification), formatLocalizedString(b.classification)];
            return formatted[0].localeCompare(formatted[1]);
          });

          // Sort classified sub-group items by their weight
          comparisonHoldings.sort((a, b) => b.weight - a.weight);

          for (const comparisonHolding of comparisonHoldings) {
            let index: number = 0;

            const classes = [ProposalStyleClassNames.ProposalTableContent] as string[];
            classes.push(rowIndex % 2 ? ProposalStyleClassNames.ProposalOdd : ProposalStyleClassNames.ProposalEven);

            this.addField(comparisonHolding.name, classes);

            if (showSymbol) {
              const comparisonHoldingSymbol = comparisonHolding.symbol || '';
              this.addString(itemDAO, comparisonHoldingSymbol, index++, classes);
            }

            if (showFundCode) {
              if (comparisonHolding.symbol !== undefined) {
                this.addString(itemDAO, comparisonHolding.symbol, index++, classes);
              } else {
                this.addNACell(itemDAO, index++, classes);
              }
            }

            if (showRiskRating) {
              if (comparisonHolding.riskRating && Object.keys(comparisonHolding.riskRating).length > 0) {
                this.addStringValue(itemDAO, comparisonHolding.riskRating, index++, classes);
              } else {
                this.addNACell(itemDAO, index++, classes);
              }
            }

            if (showAssetClass) {
              const localizedAssetClass =
                comparisonHolding.type !== HOLDING_TYPES.ALT && comparisonHolding.assetGroup
                  ? generateLocalizedString(`AssetGroups.${comparisonHolding.assetGroup}`)
                  : comparisonHolding.classification;
              this.addStringValue(itemDAO, localizedAssetClass, index++, classes);
            }

            if (showClassification) {
              this.addStringValue(itemDAO, comparisonHolding.classification, index++, classes);
            }

            totalWeightFieldIndex = index++;
            this.addPercentValue(itemDAO, comparisonHolding.weight, totalWeightFieldIndex, 2, classes);

            const dollars = round(((itemDAO.item.size || 0) * comparisonHolding.weight) / 100, 4);
            if (showAmount) {
              totalAmountFieldIndex = index++;
              this.addDollarsValue(itemDAO, dollars, totalAmountFieldIndex, 2, classes);
            }

            weightTotal += comparisonHolding.weight;
            amountTotal += dollars;

            growthStartFieldIndex = index;
            index = this.addPercentGrowths(itemDAO, comparisonHolding, index, classes);

            if (showInceptionDate) {
              const date = get(comparisonHolding.dates, 'inception', null);
              const holdingField: ProposalFieldDAO = new ProposalFieldDAO(
                this.formatMessage('Comparison.KeyMetrics.InceptionDate'),
                date,
                this.mergeClasses(classes),
              );

              holdingField.cellType = date ? DateCell : NotApplicableCell;
              this.addFieldDAO(itemDAO, holdingField, index++);
            }

            if (showMER) {
              merFieldIndex = index++;
              this.addMER(itemDAO, comparisonHolding, merFieldIndex, classes);
            }

            rowIndex++;
          }

          // do not show the total if there is no holdings
          if (rowIndex > 0) {
            const nameKey = `Proposal.Totals.${key}`;
            let fieldName: string = (this.labelOverride && this.labelOverride[nameKey] && this.labelOverride[nameKey][locale]) || this.formatMessage(nameKey);

            if (fieldAsterisks.includes(key)) {
              fieldName = `*${fieldName}`;
            }

            // showAssetGroupTotals will hide all the total(Equity ,Cash, Fixed Income) except Portfolio total
            if (showAssetGroupTotals) {
              this.addFieldName(fieldName, ProposalStyleClassNames.ProposalTableTotal);
              const field: ProposalFieldDAO = new ProposalFieldDAO(key, '', ProposalStyleClassNames.ProposalTableTotal);
              this.padItem(itemDAO, field);

              // Add the total
              const totalWeightField: ProposalFieldDAO = itemDAO.getFieldDAOs(totalWeightFieldIndex)[itemDAO.getFieldDAOs(totalWeightFieldIndex).length - 1];
              totalWeightField.value = this.formatPercentage(weightTotal, 2);

              if (showAmount) {
                const totalAmountField: ProposalFieldDAO = itemDAO.getFieldDAOs(totalAmountFieldIndex)[itemDAO.getFieldDAOs(totalAmountFieldIndex).length - 1];
                totalAmountField.value = formatCurrency(amountTotal, 2, undefined, false, this.locale);
              }
            }

            grandTotalAmount += amountTotal;
          }
        }

        // Add the grand Total
        const portfolioKey = 'Proposal.Totals.Portfolio';
        const grandTotalLabel: string =
          (this.labelOverride && this.labelOverride[portfolioKey] && this.labelOverride[portfolioKey][locale]) || this.formatMessage(portfolioKey);
        const grandTotalClassName = `${ProposalStyleClassNames.ProposalTableTotal} ${ProposalStyleClassNames.ProposalTableGrandTotal}`;
        this.addFieldName(grandTotalLabel, grandTotalClassName);

        let grandTotalField: ProposalFieldDAO = new ProposalFieldDAO(grandTotalLabel, '', grandTotalClassName);
        this.padItem(itemDAO, grandTotalField);

        grandTotalField = itemDAO.getFieldDAOs(totalWeightFieldIndex)[itemDAO.getFieldDAOs(totalWeightFieldIndex).length - 1];
        grandTotalField.value = 100;
        grandTotalField.cellType = PercentageCell;

        if (showAmount) {
          const grandTotalAmountField: ProposalFieldDAO = itemDAO.getFieldDAOs(totalAmountFieldIndex)[itemDAO.getFieldDAOs(totalAmountFieldIndex).length - 1];
          grandTotalAmountField.value = formatCurrency(grandTotalAmount, 2, undefined, false, this.locale);
        }

        if (showAllPortfolioTotals) {
          let localGrowthIndex = growthStartFieldIndex;

          const growthData: DataPointResponse<PercentGrowthHasDisclaimer> = DataPoints.getPercentGrowths(
            itemDAO.item,
            this.proposal.showBaseCurrencies === true,
            undefined,
          );

          for (const growthPeriod of this.growthPeriods) {
            const grandTotalGrowthField = itemDAO.getFieldDAOs(localGrowthIndex)[itemDAO.getFieldDAOs(localGrowthIndex).length - 1];
            const value = growthData.response[growthPeriod] && growthData.response[growthPeriod].value;

            if (value != null) {
              grandTotalGrowthField.cellType = GrowthCell;
              grandTotalGrowthField.value = value;
            } else {
              grandTotalGrowthField.cellType = NoDataCell;
            }

            localGrowthIndex++;
          }

          if (showMER) {
            const grandTotalMERField = itemDAO.getFieldDAOs(merFieldIndex)[itemDAO.getFieldDAOs(merFieldIndex).length - 1];
            const merResponse: DataPointResponse<MERBreakdown> = DataPoints.getMer(itemDAO.item);
            const merValue = usePortfolioTotalFee ? merResponse.response.total : merResponse.response.mer;

            if (merValue != null) {
              grandTotalMERField.cellType = PercentageCell;
              grandTotalMERField.value = merValue;
            } else {
              grandTotalMERField.cellType = NoDataCell;
            }
          }
        }
      } else {
        /*
         * This section is used in Top 5/10 Funds By Allocations
         */

        const dpResponse: DataPointResponse<ComparisonHolding[]> = DataPoints.getItemHoldings(itemDAO.item, hasClassEnabled);
        const holdings = this.getFilteredHoldings(dpResponse.response, filterHoldingType);

        if (holdings.length !== 0) {
          this.addDisclaimerKeys(dpResponse.disclaimerKeys, itemDAO.item);
        }

        for (let i = start; i < start + filterCount; i++) {
          const holding = holdings[i - 1];

          if (holding !== undefined) {
            const name: string = holding.name !== undefined ? this.formatLocalizedString(holding.name) : '';
            if (addFundName) {
              this.addItemField(itemDAO, name, { weight: holding.weight, name }, HoldingCell);
            } else {
              this.addItemField(itemDAO, name, holding.weight, PercentageCell);
            }

            if (this.customFieldNames === null) {
              this.addFieldName(name, ProposalStyleClassNames.ProposalTableContent);
            }
          } else {
            this.addItemField(itemDAO, '', {}, NotApplicableCell);

            if (this.customFieldNames === null) {
              this.addFieldName('', ProposalStyleClassNames.ProposalTableContent);
            }
          }
        }
      }
    }
  }

  // private Methods
  addMER(itemDAO: ComparisonItemDAO, comparisonHolding: ComparisonHolding, index: number, classes?: string[]) {
    const dpResponse: DataPointResponse<number | undefined> = DataPoints.getComparisonHoldingMER(comparisonHolding);
    const rawValue: number | undefined = dpResponse.response;
    if (rawValue === undefined) {
      this.addNACell(itemDAO, index, classes);
    } else {
      this.addPercentValue(itemDAO, rawValue, index, 2, classes);
      this.addDisclaimerKeys(dpResponse.disclaimerKeys, itemDAO.item);
    }
  }

  addPercentGrowths(itemDAO: ComparisonItemDAO, comparisonHolding: ComparisonHolding, index: number, classes: string[] = []): number {
    const dpResponse: DataPointResponse<PercentGrowth> = DataPoints.getHoldingPercentGrowths(comparisonHolding, this.proposal.showBaseCurrencies === true);
    const percentGrowth: PercentGrowth = dpResponse.response;
    let localIndex = index;

    for (const key of this.growthPeriods) {
      const rawGrowth: number | null = percentGrowth[key];
      if (rawGrowth != null) {
        this.addGrowthValue(itemDAO, rawGrowth, localIndex, [...classes, `growth-${key}`]);
        this.addDisclaimerKeys(dpResponse.disclaimerKeys, itemDAO.item);
      } else {
        this.addNACell(itemDAO, localIndex, [...classes, `growth-${key}`]);
      }
      localIndex++;
    }

    return localIndex;
  }

  addField(rawString: LocalizedString | undefined, classes: string[] = []) {
    if (rawString !== undefined) {
      const str: string = formatLocalizedString(rawString, this.locale);
      this.addFieldName(str, this.mergeClasses(classes));
    }
  }

  addStringValue(itemDAO: ComparisonItemDAO, rawString: LocalizedString | undefined, index: number, classes: string[] = []) {
    let str: string = '';
    if (rawString !== undefined) {
      str = formatLocalizedString(rawString, this.locale);
    }
    const holdingField: ProposalFieldDAO = new ProposalFieldDAO(str, str, this.mergeClasses(classes));
    this.addFieldDAO(itemDAO, holdingField, index);
  }

  addString(itemDAO: ComparisonItemDAO, rawString: string | undefined, index: number, classes: string[] = []) {
    let str: string = '';
    if (rawString !== undefined) {
      str = rawString;
    }
    const holdingField: ProposalFieldDAO = new ProposalFieldDAO(str, str, this.mergeClasses(classes));
    this.addFieldDAO(itemDAO, holdingField, index);
  }

  /* eslint-disable-next-line class-methods-use-this */
  addPercentValue(itemDAO: ComparisonItemDAO, value: number, index: number, decimals: number = 2, classes: string[] = []) {
    const holdingField: ProposalFieldDAO = new ProposalFieldDAO('', this.formatPercentage(value, decimals), this.mergeClasses(classes));
    this.addFieldDAO(itemDAO, holdingField, index);
  }

  addDollarsValue(itemDAO: ComparisonItemDAO, value?: number, index?: number, decimals: number = 2, classes: string[] = []) {
    let strValue = '';
    if (value != null) {
      strValue = formatCurrency(value, decimals, undefined, false, this.locale);
    }

    const holdingField: ProposalFieldDAO = new ProposalFieldDAO('', strValue, this.mergeClasses(classes));
    this.addFieldDAO(itemDAO, holdingField, index);
  }

  /* eslint-disable-next-line class-methods-use-this */
  addGrowthValue(itemDAO: ComparisonItemDAO, value: number, index: number, classes: string[] = []) {
    if (value > 0) {
      classes.push('positive');
    } else if (value < 0) {
      classes.push('negative');
    }

    const holdingField: ProposalFieldDAO = new ProposalFieldDAO('', this.formatPercentage(value, 2), this.mergeClasses(classes));
    this.addFieldDAO(itemDAO, holdingField, index);
  }

  /* eslint-disable-next-line class-methods-use-this */
  addNACell(itemDAO: ComparisonItemDAO, index: number, classes: string[] = []) {
    const naField: ProposalFieldDAO = new ProposalFieldDAO('', '', this.mergeClasses(classes.concat(ProposalStyleClassNames.ProposalNAValue)));
    naField.cellType = NotApplicableCell;
    this.addFieldDAO(itemDAO, naField, index);
  }

  /* eslint-disable-next-line class-methods-use-this */
  padItem(itemDAO: ComparisonItemDAO, field: ProposalFieldDAO) {
    this.addFieldDAO(itemDAO, field, 0);
    if (this.customColumnNames !== null) {
      for (let index: number = 1; index < this.customColumnNames.columns.length; index++) {
        this.addFieldDAO(itemDAO, deepClone(field), index);
      }
    }
  }

  mergeClasses = (classes: string[]) => classes.join(' ');

  getDonutData(index: number = 0): DonutData | null {
    const out: DonutData | null = super.getDonutData(index);

    if (out && this.colorsDict) {
      out.colorsDict = this.colorsDict;
    }
    return out;
  }

  private addItemField(itemDAO: ComparisonItemDAO, name: string, value: any, celltype: any): number {
    const field: ProposalFieldDAO = new ProposalFieldDAO(name, value, ProposalStyleClassNames.ProposalTableContent);
    field.cellType = celltype;
    this.addFieldDAO(itemDAO, field);
    return value;
  }

  /**
   * Returns holdings filtered by type and exclusion symbols.
   * Merges static excluded symbols with dynamic appConfig list (appConfig.proposal.filterTopFundsSymbols).
   * Falls back gracefully if config path missing.
   */
  private getFilteredHoldings(holdings: ComparisonHolding[], filterHoldingType: PortfolioHoldingType[]): ComparisonHolding[] {
    if (filterHoldingType.length <= 0) {
      return holdings;
    }

    // "Cash" and "Cash (Return-Free)" (do not count toward top holdings analysis modules)
    const EXCLUDED_HOLDING_SYMBOLS: Set<string> = new Set([
      // Populate with stable symbols when available; For proxy see the config part
      'CASH',
      'CASH-RF',
    ]);
    // Exclusions from config (enterprise settings)
    const configExcludedHoldingSymbols: string[] = get(this.proposal, 'user.appConfig.proposal.excludedTopFundsSymbols', []) as string[];

    // Merged symbols exclusions
    const mergedExcluded = new Set<string>([...EXCLUDED_HOLDING_SYMBOLS, ...configExcludedHoldingSymbols]);

    return holdings.filter(holding => {
      if (holding === null) return false;
      if (holding.symbol && mergedExcluded.has(holding.symbol)) return false;
      return filterHoldingType.includes(holding.type);
    });
  }
}
